import { PAGE_IDS, pageSpec, type PageId } from '@/features/storefront/sections/pages';

/**
 * What a theme's *words* are, as opposed to its markup.
 *
 * Sections were the first half of "a shop is data": a merchant arranges bands
 * on a page and the storefront renders what the document says. This module is
 * the other half. A theme is still full of writing that no section owns - the
 * empty basket's line, the checkout's field labels, the four service promises
 * in Hyper's footer, the three long reads under its product page - and until
 * now the only way to change any of it was to edit the theme's source, which
 * every merchant on that theme shares.
 *
 * So the copy is declared here instead: every editable string a theme renders,
 * named, grouped by the page it appears on, and carrying the words the theme
 * ships with. A merchant's changes are stored against those names in their own
 * theme document - see `document.ts` - and nothing about the theme's files
 * changes when they save. Two shops wearing Hyper have two sets of overrides
 * and one set of defaults.
 *
 * Nothing in here renders and nothing imports React, for the same reason
 * `previewThemes/meta.ts` does not: the customiser needs every theme's
 * manifest to build its editor, and pulling nine themes' components into that
 * screen to read some strings would be absurd.
 */

/**
 * Where a piece of copy appears, as the merchant would look for it.
 *
 * The customisable pages, plus the two pieces of chrome that are worn by all
 * of them and belong to no page, plus `global` for the handful of words that
 * are neither - a currency note, a "loading" line - and would otherwise be
 * filed somewhere arbitrary.
 */
export type ContentAreaId = PageId | 'header' | 'footer' | 'global';

/** Every area, in the order the content editor lists them. */
export const CONTENT_AREA_IDS: readonly ContentAreaId[] = [
  'header',
  'footer',
  ...PAGE_IDS,
  'global',
];

/** What an area is called in the editor. Pages borrow the page picker's name. */
export function contentAreaLabel(area: ContentAreaId): string {
  if (area === 'header') return 'Header';
  if (area === 'footer') return 'Footer';
  if (area === 'global') return 'Everywhere';
  return pageSpec(area).label;
}

/**
 * What kind of writing a field holds.
 *
 * Not decoration: it is how a merchant hunting for "the words on that button"
 * narrows a manifest of three hundred fields to the nine it could be. The list
 * is the kinds of hard-coded text a storefront actually contains, which is why
 * "empty state" and "validation" are their own kinds rather than both being
 * filed as messages - they are found by different questions.
 */
export type ContentRole =
  | 'heading'
  | 'label'
  | 'button'
  | 'placeholder'
  | 'message'
  | 'empty'
  | 'validation'
  | 'promo'
  | 'legal';

export const CONTENT_ROLE_LABELS: Record<ContentRole, string> = {
  heading: 'Headings',
  label: 'Labels',
  button: 'Buttons',
  placeholder: 'Placeholders',
  message: 'Messages',
  empty: 'Empty states',
  validation: 'Validation',
  promo: 'Promotions',
  legal: 'Fine print',
};

/** One editable string. */
export interface ContentField {
  /**
   * Its name in the document, without the namespace the manifest is declared
   * under. `cart.summary.heading`, not `shop.cart.summary.heading` - see
   * {@link ContentManifest}, which is what prefixes it.
   */
  key: string;
  /** What the field is called in the editor. */
  label: string;
  /** The words the theme ships. What renders until a merchant changes it. */
  value: string;
  role: ContentRole;
  /** One line of guidance, where the label alone would not place it. */
  hint?: string;
  /** True for copy that wants more than a single line of input. */
  multiline?: boolean;
  /**
   * The cap the editor enforces, matching what the design can actually hold.
   * A button captioned with a paragraph is a broken button, not a long one.
   */
  max?: number;
}

/** A handful of fields a merchant would look for in one place. */
export interface ContentGroup {
  /** Unique within a manifest. */
  id: string;
  area: ContentAreaId;
  label: string;
  /** What this corner of the page is, when the label is not enough. */
  description?: string;
  fields: readonly ContentField[];
}

/**
 * One source of copy: the shop's own, or one theme's.
 *
 * The namespace is what keeps two themes' words apart in a single flat
 * document. Hyper's product promises are `hyper.product.promises.*` and the
 * shop's shared cart lines are `shop.cart.*`, so a merchant who tries Voltic
 * for an afternoon and goes back to Hyper still has their Hyper copy, and a
 * shop wearing Hyper's family with Voltic's footer resolves each half against
 * the theme that draws it.
 */
export interface ContentManifest {
  /** A theme's id, or `shop` for the storefront's own shared copy. */
  namespace: string;
  groups: readonly ContentGroup[];
}

/** The namespace the shop's own shared copy is filed under. */
export const SHOP_NAMESPACE = 'shop';

/** A field's full name in a theme document. */
export function contentKey(namespace: string, key: string): string {
  return `${namespace}.${key}`;
}

/** Every default in one manifest, by full key. */
export function manifestDefaults(manifest: ContentManifest): Record<string, string> {
  const defaults: Record<string, string> = {};
  for (const group of manifest.groups) {
    for (const field of group.fields) {
      defaults[contentKey(manifest.namespace, field.key)] = field.value;
    }
  }
  return defaults;
}

/** Every field in one manifest, paired with the group it stands in. */
export function manifestFields(
  manifest: ContentManifest,
): ReadonlyArray<{ group: ContentGroup; field: ContentField; key: string }> {
  return manifest.groups.flatMap((group) =>
    group.fields.map((field) => ({
      group,
      field,
      key: contentKey(manifest.namespace, field.key),
    })),
  );
}
